/** @jsxImportSource @emotion/react */
import css from '@emotion/css/macro'
import styled from '@emotion/styled'
import React, { useState, useEffect, useRef, useCallback } from 'react'
import Text from '../../Text'
import Button from '../../Button'
import { TextInput } from '../../TextInput'
import ProgressHeader from '../components/ProgressHeader'
import {
  useCompetitionSetup,
  Conference,
  Division,
} from '../context/CompetitionSetupContext'
import TrophyIcon from '../icons/TrophyIcon'
import ConferenceDivisionsIcon from '../icons/ConferenceDivisionsIcon'
import ChevronDownIcon from '../icons/ChevronDownIcon'
import ChevronRightIcon from '../icons/ChevronRightIcon'
import MoreIcon from '../icons/MoreIcon'
import PlusCircleIcon from '../icons/PlusCircleIcon'
import colors from '@sportninja/common/constants/appColors'
import { Dropdown } from 'antd'
import { trackCompetitionConference } from 'src/utils/analytics'
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core'
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import { useSortable } from '@dnd-kit/sortable'
import { EditOutlined, DeleteOutlined, MenuOutlined } from '@ant-design/icons'

const StepContainer = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  height: 100%;
`

const ContentContainer = styled.div`
  flex: 1;
  padding: 16px;
  display: flex;
  flex-direction: column;
  align-items: center;
  max-width: 700px;
  margin: 0 auto;
  width: 100%;
  height: 100%;
  overflow-y: auto;

  @media (max-width: 768px) {
    padding: 16px;
    max-height: calc(100vh - 120px);
    overflow-y: auto;
  }
`

const TitleSection = styled.div`
  text-align: center;
  display: flex;
  flex-direction: column;
  gap: 12px;
`

const ScrollableContent = styled.div`
  flex: 1;
  width: 100%;
  overflow-y: auto;
  margin: 24px 0;
  min-height: 0;
`

const CompetitionContainer = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 8px;
  position: relative;
`

const CompetitionHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px;
  border-radius: 8px;
  cursor: pointer;
  transition: background 0.2s ease;

  &:hover {
    background: ${colors.SECONDARY_200};
  }
`

const ExpandButton = styled.button`
  background: transparent;
  border: none;
  cursor: pointer;
  padding: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
`

const CompetitionIcon = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
`

const ConferencesList = styled.div<{ isAddingNew: boolean }>`
  display: flex;
  flex-direction: column;
  gap: 8px;
  position: relative;
`

const ConferenceContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`

const ConferenceItem = styled.div<{
  isDragging?: boolean
  isEditing?: boolean
}>`
  background: ${colors.SECONDARY_300};
  border-radius: 8px;
  padding: 8px 8px 8px ${(props) => (props.isEditing ? '0px' : '12px')};
  display: flex;
  align-items: center;
  gap: ${(props) => (props.isEditing ? '8px' : '12px')};
  opacity: ${(props) => (props.isDragging ? 0.5 : 1)};
  cursor: move;
  transition: all 0.2s ease;
  position: relative;

  &:hover {
    background: ${colors.SECONDARY_200};
  }
`

const DragHandle = styled.div`
  color: ${colors.NEUTRAL_100};
  cursor: grab;
  display: flex;
  align-items: center;

  &:active {
    cursor: grabbing;
  }
`

const ConferenceContent = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 4px;
`

const ConferenceActions = styled.div`
  display: flex;
  align-items: center;
`

const ConferenceExpandButton = styled.button`
  background: transparent;
  border: none;
  color: ${colors.NEUTRAL_100};
  cursor: pointer;
  padding: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 4px;

  &:hover {
    background: ${colors.SECONDARY_400};
  }
`

const DivisionsList = styled.div<{ isAddingDivision: boolean }>`
  display: flex;
  flex-direction: column;
  gap: 8px;
  position: relative;
  padding-left: 24px;
`

const DivisionItem = styled.div<{ isDragging?: boolean; isEditing?: boolean }>`
  background: ${colors.SECONDARY_200};
  border-radius: 8px;
  padding: 8px 8px 8px ${(props) => (props.isEditing ? '0px' : '12px')};
  display: flex;
  align-items: center;
  gap: ${(props) => (props.isEditing ? '8px' : '12px')};
  opacity: ${(props) => (props.isDragging ? 0.5 : 1)};
  cursor: move;
  transition: all 0.2s ease;
  position: relative;

  &:hover {
    background: ${colors.SECONDARY_400};
  }
`

const DivisionContent = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 4px;
`

const DivisionActions = styled.div`
  display: flex;
  align-items: center;
`

const AddButton = styled.button`
  background: ${colors.SECONDARY_300};
  border: none;
  border-radius: 12px;
  padding: 16px 10px;
  width: 100%;
  display: flex;
  align-items: center;
  gap: 12px;
  cursor: pointer;
  transition: all 0.2s ease;
  position: relative;

  &:hover {
    background: ${colors.SECONDARY_200};
  }

  svg {
    width: 20px;
    height: 20px;
  }
`

const InputContainer = styled.div<{ isEditing: boolean; isAddingNew: boolean }>`
  display: flex;
  flex-direction: column;
  gap: 8px;
  width: 100%;
  position: relative;
  background-color: ${colors.SECONDARY_300};
  border-radius: 12px;
  padding: ${(props) =>
    props.isAddingNew
      ? props.isEditing
        ? '0 8px 8px 8px'
        : '0 8px 8px 8px'
      : props.isEditing
      ? '0 8px 8px 8px'
      : '8px'};
`

const ButtonContainer = styled.div`
  display: flex;
  gap: 12px;
  width: 100%;
`

const ActionsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  align-items: center;
  width: 100%;
  flex-shrink: 0;
  border-top: 1px solid ${colors.NEUTRAL_700};
  background: ${colors.SECONDARY_500};
`

const SkipLink = styled.button`
  background: none;
  border: none;
  color: ${colors.PRIMARY_300};
  text-decoration: underline;
  cursor: pointer;
  font-size: 14px;

  &:hover {
    color: ${colors.PRIMARY_200};
  }
`

interface DraggableDivisionProps {
  division: Division
  conferenceId: string
  onEdit: (conferenceId: string, divisionId: string) => void
  onDelete: (conferenceId: string, divisionId: string) => void
  isEditing: boolean
  onSaveEdit: () => void
  onCancelEdit: () => void
  tempEditingName: string
  setTempEditingName: (name: string) => void
}

const DraggableDivision: React.FC<DraggableDivisionProps> = ({
  division,
  conferenceId,
  onEdit,
  onDelete,
  isEditing,
  onSaveEdit,
  onCancelEdit,
  tempEditingName,
  setTempEditingName,
}) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: `${conferenceId}-${division.id}` })

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  }

  const menuItems = [
    {
      key: 'edit',
      label: 'Edit',
      icon: <EditOutlined />,
      onClick: () => onEdit(conferenceId, division.id),
    },
    {
      key: 'delete',
      label: 'Delete',
      icon: <DeleteOutlined />,
      danger: true,
      onClick: () => onDelete(conferenceId, division.id),
    },
  ]

  return (
    <div ref={setNodeRef} style={style}>
      <DivisionItem isDragging={isDragging} isEditing={isEditing}>
        <DragHandle {...attributes} {...listeners}>
          {!isEditing ? <MenuOutlined /> : null}
        </DragHandle>
        <DivisionContent>
          {isEditing ? (
            <InputContainer isEditing={isEditing} isAddingNew={false}>
              <TextInput
                name='edit_division'
                value={tempEditingName}
                setValue={setTempEditingName}
                fieldErrors={{}}
                customError={null}
                label=''
                placeholder='Division name'
              />
              <ButtonContainer>
                <Button
                  text='Save'
                  onClick={onSaveEdit}
                  variant='primary'
                  style={css`
                    flex: 1;
                  `}
                />
                <Button
                  text='Cancel'
                  onClick={onCancelEdit}
                  variant='secondary'
                  style={css`
                    flex: 1;
                  `}
                />
              </ButtonContainer>
            </InputContainer>
          ) : (
            <>
              <Text
                variant='paragraphSmall'
                weight='regular'
                color={colors.NEUTRAL_0}
              >
                {division.name}
              </Text>
              <Text
                variant='paragraphXSmall'
                weight='regular'
                color={colors.NEUTRAL_50}
              >
                Division
              </Text>
            </>
          )}
        </DivisionContent>
        {!isEditing && (
          <DivisionActions>
            <Dropdown menu={{ items: menuItems }} placement='bottomRight'>
              <button
                css={css`
                  background: transparent;
                  border: none;
                  color: ${colors.PRIMARY_300};
                  cursor: pointer;
                  padding: 4px 8px;
                  display: flex;
                  align-items: center;
                  justify-content: center;
                  border-radius: 4px;

                  &:hover {
                    background: ${colors.SECONDARY_400};
                  }
                `}
              >
                <MoreIcon />
              </button>
            </Dropdown>
          </DivisionActions>
        )}
      </DivisionItem>
    </div>
  )
}

interface DraggableConferenceProps {
  conference: Conference
  onEdit: (id: string) => void
  onDelete: (id: string) => void
  isEditing: boolean
  onSaveEdit: () => void
  onCancelEdit: () => void
  tempEditingName: string
  setTempEditingName: (name: string) => void
  isExpanded: boolean
  onToggleExpanded: (id: string) => void
  onAddDivision: (conferenceId: string) => void
  onEditDivision: (conferenceId: string, divisionId: string) => void
  onDeleteDivision: (conferenceId: string, divisionId: string) => void
  addingDivisionId: string | null
  newDivisionName: string
  setNewDivisionName: (name: string) => void
  onSaveDivision: (conferenceId: string) => void
  onCancelAddDivision: () => void
  editingDivisionId: string | null
  tempDivisionEditingName: string
  setTempDivisionEditingName: (name: string) => void
  onSaveDivisionEdit: () => void
  onCancelDivisionEdit: () => void
  onDivisionDragEnd: (event: DragEndEvent, conferenceId: string) => void
  newDivisionInputRefs: React.MutableRefObject<{ [key: string]: any }>
  handleDivisionKeyDown: (e: React.KeyboardEvent, conferenceId: string) => void
}

const DraggableConference: React.FC<DraggableConferenceProps> = ({
  conference,
  onEdit,
  onDelete,
  isEditing,
  onSaveEdit,
  onCancelEdit,
  tempEditingName,
  setTempEditingName,
  isExpanded,
  onToggleExpanded,
  onAddDivision,
  onEditDivision,
  onDeleteDivision,
  addingDivisionId,
  newDivisionName,
  setNewDivisionName,
  onSaveDivision,
  onCancelAddDivision,
  editingDivisionId,
  tempDivisionEditingName,
  setTempDivisionEditingName,
  onSaveDivisionEdit,
  onCancelDivisionEdit,
  onDivisionDragEnd,
  newDivisionInputRefs,
  handleDivisionKeyDown,
}) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: conference.id })

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  }

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  )

  const menuItems = [
    {
      key: 'edit',
      label: 'Edit',
      icon: <EditOutlined />,
      onClick: () => onEdit(conference.id),
    },
    {
      key: 'delete',
      label: 'Delete',
      icon: <DeleteOutlined />,
      danger: true,
      onClick: () => onDelete(conference.id),
    },
  ]

  return (
    <div ref={setNodeRef} style={style}>
      <ConferenceContainer>
        <ConferenceItem isDragging={isDragging} isEditing={isEditing}>
          <DragHandle {...attributes} {...listeners}>
            {!isEditing ? <MenuOutlined /> : null}
          </DragHandle>
          <ConferenceContent>
            {isEditing ? (
              <InputContainer isEditing={isEditing} isAddingNew={false}>
                <TextInput
                  name='edit_conference'
                  value={tempEditingName}
                  setValue={setTempEditingName}
                  fieldErrors={{}}
                  customError={null}
                  label=''
                  placeholder='Conference name'
                />
                <ButtonContainer>
                  <Button
                    text='Save'
                    onClick={onSaveEdit}
                    variant='primary'
                    style={css`
                      flex: 1;
                    `}
                  />
                  <Button
                    text='Cancel'
                    onClick={onCancelEdit}
                    variant='secondary'
                    style={css`
                      flex: 1;
                    `}
                  />
                </ButtonContainer>
              </InputContainer>
            ) : (
              <>
                <Text
                  variant='paragraphMedium'
                  weight='semiBold'
                  color={colors.NEUTRAL_0}
                >
                  {conference.name}
                </Text>
                <Text
                  variant='paragraphXSmall'
                  weight='regular'
                  color={colors.NEUTRAL_50}
                >
                  Conference • {conference.divisions.length} divisions
                </Text>
              </>
            )}
          </ConferenceContent>
          {!isEditing && (
            <ConferenceActions>
              <ConferenceExpandButton
                onClick={() => onToggleExpanded(conference.id)}
              >
                {isExpanded ? (
                  <ChevronDownIcon color={colors.NEUTRAL_100} size={20} />
                ) : (
                  <ChevronRightIcon color={colors.NEUTRAL_100} size={20} />
                )}
              </ConferenceExpandButton>
              <Dropdown menu={{ items: menuItems }} placement='bottomRight'>
                <button
                  css={css`
                    background: transparent;
                    border: none;
                    color: ${colors.PRIMARY_300};
                    cursor: pointer;
                    padding: 4px 8px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    border-radius: 4px;

                    &:hover {
                      background: ${colors.SECONDARY_400};
                    }
                  `}
                >
                  <MoreIcon />
                </button>
              </Dropdown>
            </ConferenceActions>
          )}
        </ConferenceItem>

        {isExpanded && !isEditing && (
          <DivisionsList isAddingDivision={addingDivisionId === conference.id}>
            <DndContext
              sensors={sensors}
              collisionDetection={closestCenter}
              onDragEnd={(event) => onDivisionDragEnd(event, conference.id)}
            >
              <SortableContext
                items={conference.divisions.map(
                  (d) => `${conference.id}-${d.id}`
                )}
                strategy={verticalListSortingStrategy}
              >
                {conference.divisions.map((division) => (
                  <DraggableDivision
                    key={division.id}
                    division={division}
                    conferenceId={conference.id}
                    onEdit={onEditDivision}
                    onDelete={onDeleteDivision}
                    isEditing={editingDivisionId === division.id}
                    onSaveEdit={onSaveDivisionEdit}
                    onCancelEdit={onCancelDivisionEdit}
                    tempEditingName={tempDivisionEditingName}
                    setTempEditingName={setTempDivisionEditingName}
                  />
                ))}
              </SortableContext>
            </DndContext>

            {addingDivisionId === conference.id ? (
              <InputContainer isEditing={false} isAddingNew={true}>
                <TextInput
                  ref={(el) => {
                    if (el) {
                      newDivisionInputRefs.current[conference.id] = el
                    }
                  }}
                  name='new_division'
                  value={newDivisionName}
                  setValue={setNewDivisionName}
                  fieldErrors={{}}
                  customError={null}
                  label=''
                  placeholder='Enter division name'
                  onKeyDown={(e) => handleDivisionKeyDown(e, conference.id)}
                />
                <ButtonContainer>
                  <Button
                    text='Add'
                    onClick={() => onSaveDivision(conference.id)}
                    variant='primary'
                    style={css`
                      flex: 1;
                    `}
                  />
                  <Button
                    text='Cancel'
                    onClick={onCancelAddDivision}
                    variant='secondary'
                    style={css`
                      flex: 1;
                    `}
                  />
                </ButtonContainer>
              </InputContainer>
            ) : (
              <AddButton onClick={() => onAddDivision(conference.id)}>
                <PlusCircleIcon color={colors.PRIMARY_300} size={20} />
                <Text
                  variant='paragraphMedium'
                  weight='semiBold'
                  color={colors.PRIMARY_300}
                >
                  Add Division
                </Text>
              </AddButton>
            )}
          </DivisionsList>
        )}
      </ConferenceContainer>
    </div>
  )
}

/**
 * Conferences Step component - Step 2 of the competition setup wizard for conference structure
 * Allows users to create and manage conferences with nested divisions
 */
const ConferencesStep: React.FC = () => {
  const {
    state,
    setConferencesData,
    nextStep,
    previousStep,
    skipWizard,
    competitionName,
  } = useCompetitionSetup()
  const [conferences, setConferences] = useState<Conference[]>(
    state.conferencesData || []
  )
  const [isAddingConference, setIsAddingConference] = useState(false)
  const [newConferenceName, setNewConferenceName] = useState('')
  const [editingConferenceId, setEditingConferenceId] = useState<string | null>(
    null
  )
  const [tempConferenceEditingName, setTempConferenceEditingName] = useState('')
  const [isExpanded, setIsExpanded] = useState(true)
  const [expandedConferences, setExpandedConferences] = useState<Set<string>>(
    new Set()
  )
  const [addingDivisionId, setAddingDivisionId] = useState<string | null>(null)
  const [newDivisionName, setNewDivisionName] = useState('')
  const [editingDivisionId, setEditingDivisionId] = useState<string | null>(
    null
  )
  const [tempDivisionEditingName, setTempDivisionEditingName] = useState('')

  // Refs for auto-focus
  const newConferenceInputRef = useRef<any>(null)
  const newDivisionInputRefs = useRef<{ [key: string]: any }>({})

  // Expand first conference when conferences are first loaded or when first conference is created
  useEffect(() => {
    if (conferences.length > 0 && expandedConferences.size === 0) {
      setExpandedConferences(new Set([conferences[0].id]))
    }
  }, [conferences.length])

  // Auto-focus when adding new conference
  useEffect(() => {
    if (isAddingConference && newConferenceInputRef.current) {
      newConferenceInputRef.current.focus()
    }
  }, [isAddingConference])

  // Auto-focus when adding new division
  useEffect(() => {
    if (addingDivisionId && newDivisionInputRefs.current[addingDivisionId]) {
      newDivisionInputRefs.current[addingDivisionId]?.focus()
    }
  }, [addingDivisionId])

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  )

  const handleConferenceDragEnd = (event: DragEndEvent) => {
    const { active, over } = event

    if (over && active.id !== over.id) {
      setConferences((items) => {
        const oldIndex = items.findIndex((item) => item.id === active.id)
        const newIndex = items.findIndex((item) => item.id === over.id)
        const newItems = arrayMove(items, oldIndex, newIndex)
        return newItems.map((item, index) => ({ ...item, order: index }))
      })
    }
  }

  const handleDivisionDragEnd = (event: DragEndEvent, conferenceId: string) => {
    const { active, over } = event

    if (over && active.id !== over.id) {
      setConferences((items) =>
        items.map((conference) => {
          if (conference.id === conferenceId) {
            const activeId = (active.id as string).split('-')[1]
            const overId = (over.id as string).split('-')[1]
            const oldIndex = conference.divisions.findIndex(
              (div) => div.id === activeId
            )
            const newIndex = conference.divisions.findIndex(
              (div) => div.id === overId
            )
            const newDivisions = arrayMove(
              conference.divisions,
              oldIndex,
              newIndex
            )
            return {
              ...conference,
              divisions: newDivisions.map((div, index) => ({
                ...div,
                order: index,
              })),
            }
          }
          return conference
        })
      )
    }
  }

  const handleAddConference = () => {
    if (newConferenceName.trim()) {
      const newConference: Conference = {
        id: `conference_${Date.now()}`,
        name: newConferenceName.trim(),
        order: conferences.length,
        divisions: [],
      }
      setConferences([...conferences, newConference])
      setNewConferenceName('')
      setIsAddingConference(false)
      setExpandedConferences((prev) => new Set(prev).add(newConference.id))
    }
  }

  const handleConferenceKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      handleAddConference()
    }
  }

  const handleEditConference = (id: string) => {
    const conference = conferences.find((c) => c.id === id)
    if (conference) {
      setEditingConferenceId(id)
      setTempConferenceEditingName(conference.name)
    }
  }

  const handleSaveConferenceEdit = () => {
    if (tempConferenceEditingName.trim() && editingConferenceId) {
      setConferences(
        conferences.map((c) =>
          c.id === editingConferenceId
            ? { ...c, name: tempConferenceEditingName.trim() }
            : c
        )
      )
      setEditingConferenceId(null)
      setTempConferenceEditingName('')
    }
  }

  const handleCancelConferenceEdit = () => {
    setEditingConferenceId(null)
    setTempConferenceEditingName('')
  }

  const handleDeleteConference = (id: string) => {
    const confirmed = window.confirm(
      'Are you sure you want to delete this conference and all its divisions?'
    )
    if (confirmed) {
      setConferences(conferences.filter((c) => c.id !== id))
      setExpandedConferences((prev) => {
        const newSet = new Set(prev)
        newSet.delete(id)
        return newSet
      })
    }
  }

  const handleToggleConferenceExpanded = (id: string) => {
    setExpandedConferences((prev) => {
      const newSet = new Set(prev)
      if (newSet.has(id)) {
        newSet.delete(id)
      } else {
        newSet.add(id)
      }
      return newSet
    })
  }

  const handleAddDivision = (conferenceId: string) => {
    setAddingDivisionId(conferenceId)
    setNewDivisionName('')
  }

  const handleSaveDivision = (conferenceId: string) => {
    if (newDivisionName.trim()) {
      const newDivision: Division = {
        id: `division_${Date.now()}`,
        name: newDivisionName.trim(),
        order: 0,
      }

      setConferences((prev) =>
        prev.map((conference) => {
          if (conference.id === conferenceId) {
            const newDivisions = [
              ...conference.divisions,
              { ...newDivision, order: conference.divisions.length },
            ]
            return { ...conference, divisions: newDivisions }
          }
          return conference
        })
      )

      setNewDivisionName('')
      setAddingDivisionId(null)
    }
  }

  const handleDivisionKeyDown = useCallback(
    (e: React.KeyboardEvent, conferenceId: string) => {
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault()
        handleSaveDivision(conferenceId)
      }
    },
    [newDivisionName]
  )

  const handleCancelAddDivision = () => {
    setAddingDivisionId(null)
    setNewDivisionName('')
  }

  const handleEditDivision = (conferenceId: string, divisionId: string) => {
    const conference = conferences.find((c) => c.id === conferenceId)
    const division = conference?.divisions.find((d) => d.id === divisionId)
    if (division) {
      setEditingDivisionId(divisionId)
      setTempDivisionEditingName(division.name)
    }
  }

  const handleSaveDivisionEdit = () => {
    if (tempDivisionEditingName.trim() && editingDivisionId) {
      setConferences((prev) =>
        prev.map((conference) => ({
          ...conference,
          divisions: conference.divisions.map((division) =>
            division.id === editingDivisionId
              ? { ...division, name: tempDivisionEditingName.trim() }
              : division
          ),
        }))
      )
      setEditingDivisionId(null)
      setTempDivisionEditingName('')
    }
  }

  const handleCancelDivisionEdit = () => {
    setEditingDivisionId(null)
    setTempDivisionEditingName('')
  }

  const handleDeleteDivision = (conferenceId: string, divisionId: string) => {
    const confirmed = window.confirm(
      'Are you sure you want to delete this division?'
    )
    if (confirmed) {
      setConferences((prev) =>
        prev.map((conference) => {
          if (conference.id === conferenceId) {
            return {
              ...conference,
              divisions: conference.divisions.filter(
                (d) => d.id !== divisionId
              ),
            }
          }
          return conference
        })
      )
    }
  }

  const handleNext = () => {
    console.log(`🔄 ConferencesStep: Proceeding with ${conferences.length} conferences`)
    
    // Track conference setup with conference count
    trackCompetitionConference(conferences.length)
    
    setConferencesData(conferences)
    nextStep()
  }

  const handleBack = () => {
    previousStep()
  }

  const totalDivisions = conferences.reduce(
    (acc, conf) => acc + conf.divisions.length,
    0
  )

  return (
    <StepContainer>
      <ProgressHeader currentStep={2} />

      <ContentContainer>
        <TitleSection>
          <Text variant='heading3' weight='semiBold' color={colors.NEUTRAL_0}>
            Create Conferences & Divisions
          </Text>
          <Text
            variant='paragraphMedium'
            weight='regular'
            color={colors.NEUTRAL_50}
          >
            Organize your competition with conferences containing divisions.
          </Text>
        </TitleSection>

        <ScrollableContent>
          <CompetitionContainer>
            <CompetitionHeader onClick={() => setIsExpanded(!isExpanded)}>
              <ExpandButton type='button'>
                {isExpanded ? (
                  <ChevronDownIcon color={colors.NEUTRAL_100} size={24} />
                ) : (
                  <ChevronRightIcon color={colors.NEUTRAL_100} size={24} />
                )}
              </ExpandButton>
              <CompetitionIcon>
                <TrophyIcon color={colors.PRIMARY} size={40} />
              </CompetitionIcon>
              <div>
                <Text
                  variant='paragraphMedium'
                  weight='bold'
                  color={colors.NEUTRAL_0}
                >
                  {competitionName}
                </Text>
                <Text
                  variant='paragraphXSmall'
                  weight='regular'
                  color={colors.NEUTRAL_50}
                >
                  Competition • {conferences.length} Conferences •{' '}
                  {totalDivisions} Divisions
                </Text>
              </div>
            </CompetitionHeader>

            {isExpanded && (
              <ConferencesList isAddingNew={isAddingConference}>
                <DndContext
                  sensors={sensors}
                  collisionDetection={closestCenter}
                  onDragEnd={handleConferenceDragEnd}
                >
                  <SortableContext
                    items={conferences.map((c) => c.id)}
                    strategy={verticalListSortingStrategy}
                  >
                    {conferences.map((conference) => (
                      <DraggableConference
                        key={conference.id}
                        conference={conference}
                        onEdit={handleEditConference}
                        onDelete={handleDeleteConference}
                        isEditing={editingConferenceId === conference.id}
                        onSaveEdit={handleSaveConferenceEdit}
                        onCancelEdit={handleCancelConferenceEdit}
                        tempEditingName={tempConferenceEditingName}
                        setTempEditingName={setTempConferenceEditingName}
                        isExpanded={expandedConferences.has(conference.id)}
                        onToggleExpanded={handleToggleConferenceExpanded}
                        onAddDivision={handleAddDivision}
                        onEditDivision={handleEditDivision}
                        onDeleteDivision={handleDeleteDivision}
                        addingDivisionId={addingDivisionId}
                        newDivisionName={newDivisionName}
                        setNewDivisionName={setNewDivisionName}
                        onSaveDivision={handleSaveDivision}
                        onCancelAddDivision={handleCancelAddDivision}
                        editingDivisionId={editingDivisionId}
                        tempDivisionEditingName={tempDivisionEditingName}
                        setTempDivisionEditingName={setTempDivisionEditingName}
                        onSaveDivisionEdit={handleSaveDivisionEdit}
                        onCancelDivisionEdit={handleCancelDivisionEdit}
                        onDivisionDragEnd={handleDivisionDragEnd}
                        newDivisionInputRefs={newDivisionInputRefs}
                        handleDivisionKeyDown={handleDivisionKeyDown}
                      />
                    ))}
                  </SortableContext>
                </DndContext>

                {isAddingConference ? (
                  <InputContainer isEditing={false} isAddingNew={true}>
                    <TextInput
                      ref={newConferenceInputRef}
                      name='new_conference'
                      value={newConferenceName}
                      setValue={setNewConferenceName}
                      fieldErrors={{}}
                      customError={null}
                      label=''
                      placeholder='Enter conference name'
                      onKeyDown={handleConferenceKeyDown}
                    />
                    <ButtonContainer>
                      <Button
                        text='Add'
                        onClick={handleAddConference}
                        variant='primary'
                        style={css`
                          flex: 1;
                        `}
                      />
                      <Button
                        text='Cancel'
                        onClick={() => {
                          setIsAddingConference(false)
                          setNewConferenceName('')
                        }}
                        variant='secondary'
                        style={css`
                          flex: 1;
                        `}
                      />
                    </ButtonContainer>
                  </InputContainer>
                ) : (
                  <AddButton onClick={() => setIsAddingConference(true)}>
                    <ConferenceDivisionsIcon
                      color={colors.PRIMARY_300}
                      size={20}
                    />
                    <Text
                      variant='paragraphMedium'
                      weight='semiBold'
                      color={colors.PRIMARY_300}
                    >
                      Add Conference
                    </Text>
                  </AddButton>
                )}
              </ConferencesList>
            )}
          </CompetitionContainer>
        </ScrollableContent>

        <ActionsContainer>
          <div
            css={css`
              display: flex;
              gap: 16px;
              align-items: center;
              width: 100%;
            `}
          >
            <Button
              text='BACK'
              onClick={handleBack}
              variant='secondary'
              uppercase
              style={css`
                flex: 1;
              `}
            />
            <Button
              text='NEXT'
              onClick={handleNext}
              variant='primary'
              uppercase
              disabled={conferences.length === 0 || totalDivisions === 0}
              style={css`
                flex: 1;
              `}
            />
          </div>
          <SkipLink onClick={skipWizard}>Skip Setup Wizard</SkipLink>
        </ActionsContainer>
      </ContentContainer>
    </StepContainer>
  )
}

export default ConferencesStep
